import { supabase } from '@/integrations/supabase/client';

const SUPABASE_URL = import.meta.env.VITE_SUPABASE_URL;
const SUPABASE_ANON_KEY = import.meta.env.VITE_SUPABASE_PUBLISHABLE_KEY;

interface MfaInvokeOptions {
  body: Record<string, unknown>;
  altchaToken?: string | null;
}

interface MfaInvokeResult<T = Record<string, unknown>> {
  data: T | null;
  error: Error | null;
}

/**
 * Invokes the send-mfa-code edge function with the required
 * X-SS-Auth header and optional ALTCHA token that
 * supabase.functions.invoke() cannot provide.
 */
export async function invokeMfa<T = Record<string, unknown>>(
  options: MfaInvokeOptions,
): Promise<MfaInvokeResult<T>> {
  try {
    const { data: sessionData } = await supabase.auth.getSession();
    const accessToken = sessionData?.session?.access_token;

    const mergedBody = { ...options.body };
    if (options.altchaToken) {
      mergedBody.altchaToken = options.altchaToken;
    }

    const res = await fetch(`${SUPABASE_URL}/functions/v1/send-mfa-code`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'x-ss-auth': 'user',
        apikey: SUPABASE_ANON_KEY,
        ...(accessToken ? { Authorization: `Bearer ${accessToken}` } : {}),
      },
      body: JSON.stringify(mergedBody),
    });

    const text = await res.text();
    let json: T | null = null;
    try {
      json = JSON.parse(text) as T;
    } catch {
      // non-JSON response
    }

    if (!res.ok) {
      const msg =
        (json as Record<string, unknown>)?.error ??
        text ??
        `HTTP ${res.status}`;
      return { data: json, error: new Error(String(msg)) };
    }

    return { data: json, error: null };
  } catch (err) {
    return {
      data: null,
      error: err instanceof Error ? err : new Error(String(err)),
    };
  }
}
